import { useCallback, useEffect, lazy, Suspense, useState } from 'react';
import { Button, Space, Tooltip, Segmented, message, Modal, Dropdown, Typography } from 'antd';
import AppLoading from '../AppLoading';
import {
  SaveOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
  RotateRightOutlined,
  CameraOutlined,
  EyeOutlined,
  BorderOutlined,
  ExpandOutlined,
  CloudOutlined,
  PlayCircleOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined,
  AimOutlined,
  PictureOutlined,
  DownloadOutlined,
  BorderOuterOutlined,
  GroupOutlined,
} from '@ant-design/icons';
import useSceneStore from '../../stores/sceneStore';
import useEditorUiStore from '../../stores/editorUiStore';
import useDashboardStore from '../../stores/dashboardStore';
import DashboardComponentsOverlay from '../dashboard/DashboardComponentsOverlay';
import { exportScenesBackup, backupExportErrorHandler } from '../../utils/backup';
import { fetchBackupPolicy, type BackupPolicyView } from '../../api/backup';
import { cameraControlsRegistry } from '../../stores/cameraControlsStore';
import { useDeployStore } from '../../stores/deployStore';
import { useSceneSave } from '../../hooks/useSceneSave';
import DataMenu from './toolbar/DataMenu';
import OperationMenu from './toolbar/OperationMenu';
import FusionMenu from './toolbar/FusionMenu';
import RenderPresetButton from './RenderPresetButton';
import RenderInspectButton from './RenderInspectButton';
import SimulationButton from './SimulationButton';
import AiEditorPanel from './AiEditorPanel';
import ExportImageButton from './ExportImageButton';
import ModelGenPanel from './ModelGenPanel';
import RecordRoamButton from './RecordRoamButton';

const SceneCanvas = lazy(() => import('./SceneCanvas'));

// 视角预设：perspective 增加俯角使点击"透视"有明显视觉变化（与默认相机位置区分）
const VIEW_PRESETS: Record<string, { position: [number, number, number]; target: [number, number, number] }> = {
  perspective: { position: [20, 14, 20], target: [0, 0, 0] },
  top: { position: [0, 50, 0.01], target: [0, 0, 0] },
  front: { position: [0, 10, 50], target: [0, 0, 0] },
  side: { position: [50, 10, 0], target: [0, 0, 0] },
};

// 视角按钮点击后通过 cameraControlsRegistry.setView 切换相机；
// 测试方法：选中任意模型 → 点击"视角"→ 选择"前视图"，观察相机从斜向移到正面；再选"透视"观察回到 45° 斜视角。

export default function SceneToolbar() {
  const {
    currentScene,
    selectedModelId,
    transformMode,
    setTransformMode,
    cameraStrategy,
    setCameraStrategy,
    savedCamera,
    setSavedCamera,
    dirty,
  } = useSceneStore();
  const dashboardId = useEditorUiStore((s) => s.dashboardId);
  const dashPage = useDashboardStore((s) => s.currentPage);
  // 2026-10-11：预览叠加层改用实时 store.components（页面快照 currentPage.components 不随编辑更新，
  // 会导致「叠加编辑新加的组件保存后预览仍不显示，重新打开项目才出现」）
  const dashComponents = useDashboardStore((s) => s.components);
  // 2026-09-27：保存逻辑抽到 useSceneSave（保存按钮与编辑器 Ctrl+S 共用）
  const { save: handleSave, saving } = useSceneSave();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  // 导入导出权限（ZIP 默认仅超管可见；服务端强校验）
  const [backupPolicy, setBackupPolicy] = useState<BackupPolicyView | null>(null);
  useEffect(() => {
    fetchBackupPolicy().then(setBackupPolicy).catch(() => setBackupPolicy(null));
  }, []);
  // 2026-10-06：展示区域（框选有限展示范围；Esc 取消）
const displayRegion = useSceneStore((s) => s.displayRegion);
const regionSelecting = useSceneStore((s) => s.regionSelecting);
const regionGenSelecting = useSceneStore((s) => s.regionGenSelecting);

  const handlePreview = useCallback(() => {
    if (!currentScene) {
      message.info('请先保存场景再预览');
      return;
    }
    setPreviewOpen(true);
  }, [currentScene]);

  // 2026-10-06：预览弹窗打开时暂停主画布渲染（双画布同帧会掉到 2fps → 预览内点击无响应“卡死”）
  // getState 可选调用：部分单测 mock editorUiStore 未提供该 action（沿用 PropertyTreePanel 先例）
  useEffect(() => {
    const anyStore = useEditorUiStore as any;
    anyStore.getState?.()?.setScenePreviewOpen?.(previewOpen);
    return () => anyStore.getState?.()?.setScenePreviewOpen?.(false);
  }, [previewOpen]);

  // DW-P0-8：发布改为打开「项目部署」对话框（Web 链接/部署包/EXE/图片/边缘与对接 统一入口）
  const handlePublish = useCallback(() => {
    if (!currentScene) {
      message.error('当前无场景可发布');
      return;
    }
    useDeployStore.getState().openModal({ kind: 'scene', sceneId: currentScene.id, name: currentScene.name });
  }, [currentScene]);

  const handleCloud = useCallback(() => {
    message.info('云存储功能');
  }, []);

  const handleFocusSelected = useCallback(() => {
    if (!selectedModelId) {
      message.info('请先选中模型');
      return;
    }
    cameraControlsRegistry.focusSelected?.();
  }, [selectedModelId]);

  const handleResetCamera = useCallback(() => {
    cameraControlsRegistry.resetCamera?.();
  }, []);

  const handleSaveCurrentCamera = useCallback(() => {
    const cam = cameraControlsRegistry.getCamera?.();
    if (!cam) {
      message.info('当前相机不可用，请先进入场景');
      return;
    }
    setSavedCamera(cam);
    // 保存当前视角后自动切换为 current 策略
    setCameraStrategy('current');
    message.success('已保存当前视角（导入模型不再复位相机）');
  }, [setSavedCamera, setCameraStrategy]);

  const handleCameraStrategyChange = useCallback((strategy: string) => {
    setCameraStrategy(strategy as 'auto' | 'current' | 'custom');
    if (strategy === 'auto') {
      cameraControlsRegistry.autoFit?.();
    } else if (strategy === 'current' && savedCamera) {
      // 应用已保存的相机（直接读取 Canvas 侧通过 registry 应用）
      cameraControlsRegistry.applySavedCamera?.(savedCamera);
    } else if (strategy === 'custom' && savedCamera) {
      cameraControlsRegistry.applySavedCamera?.(savedCamera);
    }
  }, [setCameraStrategy, savedCamera]);

  const handleView = useCallback((view: string) => {
    cameraControlsRegistry.setView?.(view);
  }, []);

  const handleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen?.().catch(() => {});
    } else {
      document.exitFullscreen?.().catch(() => {});
    }
  }, []);

  // 全屏状态以 fullscreenchange 事件为准（覆盖：按钮进入 / 原生 ESC 退出 / 按钮退出）
  // 修复(2026-08-19)：原来靠 requestFullscreen promise resolve 同步，浏览器全屏动画期间
  // 或 ESC 退出时 state 不同步 → 按钮图标/提示一直显示"全屏"、点击行为错乱
  useEffect(() => {
    const onFsChange = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  const viewMenuItems = Object.keys(VIEW_PRESETS).map((key) => ({
    key,
    label: key === 'perspective' ? '透视' : key === 'top' ? '顶视图' : key === 'front' ? '前视图' : '侧视图',
  }));

  return (
    <div className="editor-toolbar">
      <Space size={4}>
        <Tooltip title={dirty ? '保存场景（有未保存的修改）' : '保存场景'}>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSave}
            loading={saving}
            size="small"
            style={{ background: '#1890ff', borderColor: '#1890ff' }}
          >
            保存{dirty ? '*' : ''}
          </Button>
        </Tooltip>
        <DataMenu />
        <OperationMenu />
        <FusionMenu />
        <RenderPresetButton />
        <RenderInspectButton />
        <SimulationButton />
        <Tooltip title="区域生成：拖拽框选一片模型，交给 AI 批量替换/风格统一">
          <Button
            size="small"
            type={regionGenSelecting ? 'primary' : 'default'}
            icon={<GroupOutlined />}
            data-testid="region-gen-btn"
            onClick={() => {
              const next = !regionGenSelecting;
              useSceneStore.getState().setRegionGenSelecting(next);
              if (next) message.info('拖拽框选要批量操作的模型（Esc 取消）');
            }}
          >
            区域生成
          </Button>
        </Tooltip>
        <AiEditorPanel />
        <ExportImageButton />
        <ModelGenPanel />
        <RecordRoamButton />
      </Space>

      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <Segmented
          size="small"
          options={[
            { value: 'translate', icon: <BorderOutlined />, label: '平移' },
            { value: 'rotate', icon: <RotateRightOutlined />, label: '旋转' },
            { value: 'scale', icon: <ZoomInOutlined />, label: '缩放' },
          ]}
          value={transformMode}
          onChange={(val: string | number) => setTransformMode(val as 'translate' | 'rotate' | 'scale')}
        />
      </div>

      <Space size={4}>
        <Tooltip title="聚焦选中">
          <Button icon={<CameraOutlined />} onClick={handleFocusSelected} size="small" />
        </Tooltip>
        <Dropdown
          menu={{
            items: viewMenuItems,
            onClick: ({ key }) => handleView(key),
          }}
          trigger={['click']}
        >
          <Tooltip title="视角">
            <Button icon={<EyeOutlined />} size="small" />
          </Tooltip>
        </Dropdown>
        <Dropdown
          menu={{
            items: [
              { key: 'auto', label: '初始视角（自动适配）' },
              { key: 'current', label: '当前场景视角' },
              { key: 'custom', label: '自定义（沿用已保存）' },
            ],
            onClick: ({ key }) => handleCameraStrategyChange(key),
            selectedKeys: [cameraStrategy],
          }}
          trigger={['click']}
        >
          <Tooltip title={`相机策略: ${cameraStrategy === 'auto' ? '初始视角' : cameraStrategy === 'current' ? '当前视角' : '自定义'}`}>
            <Button
              icon={<AimOutlined />}
              size="small"
              type={cameraStrategy !== 'auto' ? 'primary' : 'default'}
            />
          </Tooltip>
        </Dropdown>
        <Tooltip title="保存当前视角（之后导入/剪切/粘贴不改变相机）">
          <Button icon={<PictureOutlined />} onClick={handleSaveCurrentCamera} size="small" />
        </Tooltip>
        <Tooltip title="重置相机">
          <Button icon={<ZoomOutOutlined />} onClick={handleResetCamera} size="small" />
        </Tooltip>
        <Tooltip title={isFullscreen ? '退出全屏' : '全屏'}>
          <Button
            icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
            onClick={handleFullscreen}
            size="small"
          />
        </Tooltip>
        <Tooltip title="预览">
          <Button icon={<PlayCircleOutlined />} onClick={handlePreview} size="small" data-testid="scene-preview-btn" />
        </Tooltip>
        <Tooltip title={regionSelecting ? '框选中…（Esc 取消）' : '展示区域：点击后左键拖拽框选（区域外裁剪；预览/发布同样生效）'}>
          <Button
            icon={<BorderOuterOutlined />}
            size="small"
            type={regionSelecting ? 'primary' : 'default'}
            data-testid="display-region-tool"
            onClick={() => {
              const st = useSceneStore.getState();
              if (st.regionSelecting) {
                st.setRegionSelecting(false);
                return;
              }
              st.setRegionSelecting(true);
              message.info('鼠标左键在场景中拖拽框选「展示区域」（区域外将被裁剪；Esc 取消）');
            }}
          />
        </Tooltip>
        <Dropdown
          trigger={['click']}
          menu={{
            items: [
              { key: 'json', label: '导出 JSON（元数据）' },
              ...(backupPolicy?.zipAllowed ? [{ key: 'zip', label: '导出 ZIP（含模型文件）' }] : []),
            ],
            onClick: async ({ key }) => {
              if (!currentScene?.id) {
                message.warning('场景未就绪，请稍后再试');
                return;
              }
              try {
                const n = await exportScenesBackup(key as 'json' | 'zip', currentScene.id);
                message.success(`已导出「${currentScene.name}」（${key === 'zip' ? 'ZIP 含模型文件' : 'JSON 元数据'}）`);
                void n;
              } catch (e) {
                backupExportErrorHandler(e);
              }
            },
          }}
        >
          <Tooltip title="导出当前场景">
            <Button icon={<DownloadOutlined />} size="small" />
          </Tooltip>
        </Dropdown>
        <Tooltip title="云存储">
          <Button icon={<CloudOutlined />} onClick={handleCloud} size="small" />
        </Tooltip>
        <Tooltip title="发布">
          <Button icon={<ExpandOutlined />} onClick={handlePublish} size="small" data-testid="deploy-open" />
        </Tooltip>
      </Space>

      <Modal
        title={`预览 - ${currentScene?.name || '场景'}`}
        open={previewOpen}
        onCancel={() => setPreviewOpen(false)}
        footer={null}
        width={960}
        destroyOnHidden
      >
        <div style={{ height: 540, background: '#1a1a2e', position: 'relative' }}>
          {/* 2026-08-31｜预览黑屏期提示：预览会重新加载场景模型（FBX 约 1~2 分钟），避免误以为黑屏 */}
          <div style={{ position: 'absolute', top: 8, left: '50%', transform: 'translateX(-50%)', zIndex: 20, pointerEvents: 'none' }}>
            <Typography.Text style={{ fontSize: 12, color: '#fff', background: 'rgba(0,0,0,0.6)', padding: '4px 12px', borderRadius: 4 }}>
              模型加载中（大模型约 1~2 分钟），请稍候…
            </Typography.Text>
          </div>
          <Suspense fallback={<AppLoading />}>
            {/* 2026-10-06：预览画布改只读按需渲染（displayOnly：静置不耗帧；交互/相机变化再重绘），
                与主画布“预览时暂停”配合，解决预览卡死 */}
            {previewOpen && <SceneCanvas sceneId={currentScene?.id} displayOnly />}
            {/* 融合预览：已关联 2D 大屏时，在其上合成大屏组件层（透明，3D 保持加载） */}
            {dashboardId && dashPage && currentScene?.id && (
              <div style={{ position: 'absolute', inset: 0, zIndex: 10, pointerEvents: 'none' }}>
                <DashboardComponentsOverlay
                  components={dashComponents}
                  pageWidth={dashPage.config?.width || 1920}
                  pageHeight={dashPage.config?.height || 1080}
                  backgroundColor={dashPage.config?.backgroundColor}
                  backgroundImage={dashPage.config?.backgroundImage}
                />
              </div>
            )}
          </Suspense>
        </div>
      </Modal>
    </div>
  );
}
